import SockJS from 'sockjs-client';
import Stomp, { Client, Message, Subscription } from 'stompjs';

let stompClient: Client | null = null;

type OnConnectedCallback = () => void;
type OnErrorCallback = (error: string | Stomp.Frame) => void;
type OnMessageReceivedCallback = (message: Message) => void;

interface ConnectionHeaders {
  [key: string]: string;
}

interface SubscriptionHeaders {
  [key: string]: string;
}

interface SubscriptionRecord {
  handler: OnMessageReceivedCallback;
  headers: SubscriptionHeaders;
}

// The server treats a STOMP subscription as the membership itself: a player is
// dropped from a lobby the moment their subscription to /topic/public/lobby/{id}
// ends, and the lobby is deleted once it holds nobody. The corollary is that a
// lobby whose members are not subscribed can never be emptied — it stays in
// "Available Lobbies" forever with players in it who are long gone. So every
// subscription is recorded here and replayed after a reconnect; losing one
// silently strands whatever it was keeping alive.
const subscriptions: Record<string, Subscription> = {};
const records: Record<string, SubscriptionRecord> = {};

let reconnectAttempts = 0;
let hasConnectedOnce = false;
let manuallyDisconnected = false;

// Anything that must subscribe as soon as the app loads — an invite link auto-join,
// for instance — races the STOMP handshake. Callers await this instead of guessing
// at a delay.
let connected = false;
let connectionWaiters: Array<() => void> = [];

const markConnected = (): void => {
  connected = true;
  connectionWaiters.forEach(resolve => resolve());
  connectionWaiters = [];
};

export const isConnected = (): boolean => connected;

export const whenConnected = (): Promise<void> =>
  connected ? Promise.resolve() : new Promise<void>(resolve => connectionWaiters.push(resolve));
const MAX_BACKOFF_MS = 10000;

const closeSubscription = (channel: string): void => {
  const subscription = subscriptions[channel];
  if (!subscription) return;
  delete subscriptions[channel];
  try {
    subscription.unsubscribe();
  } catch (error) {
    // The socket is already gone; the server has dropped the subscription anyway.
  }
};

const openSubscription = (channel: string, record: SubscriptionRecord): void => {
  if (!stompClient || !connected) return;
  // Fresh headers per attempt: stompjs stamps an `id` onto the object it is
  // handed, and an id minted for a dead session must not follow us into a new one.
  subscriptions[channel] = stompClient.subscribe(channel, record.handler, { ...record.headers });
};

// Re-establishes every recorded subscription on a new session. Membership that
// the server derives from a subscription (lobbies) still needs to be re-claimed
// over REST by the caller — the server dropped it when the old socket died.
const replaySubscriptions = (): void => {
  Object.entries(records).forEach(([channel, record]) => openSubscription(channel, record));
};

const connect = (
  url: string,
  header: ConnectionHeaders,
  onConnected: OnConnectedCallback,
  onError: OnErrorCallback,
  onReconnected: OnConnectedCallback = onConnected
): void => {
  manuallyDisconnected = false;
  hasConnectedOnce = false;
  reconnectAttempts = 0;
  connected = false;

  const attemptConnect = () => {
    if (manuallyDisconnected) return;

    const socket = new SockJS(url);
    stompClient = Stomp.over(socket);
    stompClient.debug = () => {}; // Empty function
    stompClient.connect(
      header,
      () => {
        const wasReconnect = hasConnectedOnce;
        hasConnectedOnce = true;
        reconnectAttempts = 0;
        markConnected();
        // Before the callbacks, so they can publish and resync over live topics.
        replaySubscriptions();
        if (wasReconnect) {
          onReconnected();
        } else {
          onConnected();
        }
      },
      (error: string | Stomp.Frame) => {
        connected = false;
        Object.keys(subscriptions).forEach(channel => delete subscriptions[channel]);
        onError(error);
        if (manuallyDisconnected) return;
        const delay = Math.min(1000 * 2 ** reconnectAttempts, MAX_BACKOFF_MS);
        reconnectAttempts++;
        setTimeout(attemptConnect, delay);
      }
    );
  };
  attemptConnect();
};

const disconnect = (): void => {
  manuallyDisconnected = true;
  connected = false;
  Object.keys(records).forEach(channel => delete records[channel]);
  Object.keys(subscriptions).forEach(channel => delete subscriptions[channel]);
  if (stompClient !== null) {
   stompClient.disconnect(() => {}); // Provide callback
  }
};

const addSubscription = (
  channel: string,
  onMessageReceived: OnMessageReceivedCallback,
  headers: SubscriptionHeaders
): void => {
  // Re-subscribing over a live subscription would leave the old one delivering
  // into a stale handler, so every message would be handled twice.
  closeSubscription(channel);

  const record: SubscriptionRecord = { handler: onMessageReceived, headers };
  records[channel] = record;
  // Not connected yet: replaySubscriptions() opens it as soon as the session is up.
  openSubscription(channel, record);
};

const subscribe = (
  channel: string,
  onMessageReceived: OnMessageReceivedCallback
): void => {
  addSubscription(channel, onMessageReceived, {});
};

const subscribeWithPassword = (
  channel: string,
  onMessageReceived: OnMessageReceivedCallback,
  password: string
): void => {
  addSubscription(channel, onMessageReceived, { password });
};

// For lobby topics this is the "leave" signal: the server has no leave endpoint,
// it removes the player when the subscription ends.
const unsubscribe = (channel: string): void => {
  delete records[channel];
  closeSubscription(channel);
};

export const isSubscribed = (channel: string): boolean => records[channel] !== undefined;

export { connect, disconnect, subscribe, subscribeWithPassword, unsubscribe };
